import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import * as moment from "moment";
import { SapRoles } from "src/_constants/roleType";
import { AppService } from "src/_services/app.service";

@Component({
  selector: "app-operator-dashboard",
  templateUrl: "./operator-dashboard.component.html",
  styleUrls: ["./operator-dashboard.component.scss"],
})
export class OperatorDashboardComponent implements OnInit {
  userRole: any = JSON.parse(sessionStorage.getItem("currentUser"));
  sapRoles = SapRoles;
  operatorData: string[] = [];

  // Per-table operator filter (GroupAdmin)
  dailyOperator = "all";
  playerOperator = "all";

  dailySummary: any = null;
  playerRows: any[] = [];

  lastSevenDays: string[] = [];
  selectedDailyDay: string = "";
  selectedPlayerDay: string = "";

  readonly dayCount = 7;

  dailyMetricRows: { label: string; value: any; cssClass?: string }[] = [];

  constructor(private appService: AppService, private router: Router) {}

  ngOnInit() {
    this.getPastDays(this.dayCount);
    if (this.isGroupAdmin) {
      this.getOperatorData();
    }
    this.getDailySummaryForDay(this.selectedDailyDay);
    this.getTopPlayersForDay(this.selectedPlayerDay);
  }

  get isOperatorAdmin() {
    return (
      this.userRole &&
      (this.userRole.role === this.sapRoles.OperatorAdmin ||
        this.userRole.role === this.sapRoles.OperatorUser)
    );
  }

  get isGroupAdmin() {
    return (
      this.userRole &&
      (this.userRole.role === this.sapRoles.GroupAdmin ||
        this.userRole.role === this.sapRoles.GroupUser)
    );
  }

  getPastDays(days: number) {
    const pastDays: string[] = [];
    for (let i = 0; i < days; i++) {
      pastDays.push(moment().subtract(i, "days").format("YYYY-MM-DD"));
    }
    this.lastSevenDays = pastDays;
    this.selectedDailyDay = pastDays[0];
    this.selectedPlayerDay = pastDays[0];
  }

  transformDate(dateString: string) {
    return moment(dateString, "YYYY-MM-DD").format("Do MMM");
  }

  getOperatorData() {
    this.appService.getOperatorId().subscribe(
      (res) => {
        if (res.status === "RS_OK") {
          this.operatorData = (res.operators || [])
            .map((op) => op.operator_id)
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        } else if (res.errorDescription === "Invalid Token!") {
          sessionStorage.clear();
          this.router.navigateByUrl("/login");
        }
      },
      () => {}
    );
  }

  onDailyOperatorChange() {
    this.getDailySummaryForDay(this.selectedDailyDay);
  }

  onPlayerOperatorChange() {
    this.getTopPlayersForDay(this.selectedPlayerDay);
  }

  onDailyDayTabChange(index: number) {
    this.selectedDailyDay = this.lastSevenDays[index];
    this.getDailySummaryForDay(this.selectedDailyDay);
  }

  onPlayerDayTabChange(index: number) {
    this.selectedPlayerDay = this.lastSevenDays[index];
    this.getTopPlayersForDay(this.selectedPlayerDay);
  }

  // Same time encoding as Game Report page
  dayBounds(dateStr: string) {
    const start = moment(dateStr, "YYYY-MM-DD").startOf("day").toDate();
    const end = moment(dateStr, "YYYY-MM-DD").endOf("day").toDate();
    return {
      startTime: Math.floor(start.getTime() / 1000),
      endTime: Math.floor(end.getTime() / 1000),
    };
  }

  withOperatorFilter(body: any, selectedOp: string) {
    if (this.isGroupAdmin && selectedOp && selectedOp !== "all") {
      body.operatorId = selectedOp;
    }
    return body;
  }

  handleAuthError(res: any) {
    if (res && res.errorDescription === "Invalid Token!") {
      sessionStorage.clear();
      this.router.navigateByUrl("/login");
      return true;
    }
    return false;
  }

  buildDailyMetricRows(row: any) {
    if (!row) {
      this.dailyMetricRows = [];
      return;
    }
    this.dailyMetricRows = [
      { label: "Players", value: row.totalUsers || 0 },
      { label: "Total Bets", value: row.totalBets || 0 },
      { label: "Total Amount", value: this.formatNumber(row.totalAmount) },
      { label: "Total Won", value: row.totalWon || 0, cssClass: "profit-color" },
      { label: "Total Lost", value: row.totalLost || 0, cssClass: "loss-color" },
      { label: "Total Cancel", value: row.totalCancel || 0 },
      {
        label: "Profit",
        value: this.formatNumber(row.ggr),
        cssClass: row.ggr > 0 ? "profit-color" : "loss-color",
      },
    ];
  }

  getDailySummaryForDay(dateStr: string) {
    const req = this.withOperatorFilter(
      {
        ...this.dayBounds(dateStr),
      },
      this.dailyOperator
    );
    this.appService.getOpDailySummary(req).subscribe(
      (res) => {
        if (res.status === "RS_OK") {
          const rows = res.rows || [];
          this.dailySummary = rows.length
            ? rows[0]
            : {
                date: dateStr,
                totalUsers: 0,
                totalBets: 0,
                totalAmount: 0,
                totalWon: 0,
                totalLost: 0,
                totalCancel: 0,
                ggr: 0,
              };
          this.buildDailyMetricRows(this.dailySummary);
        } else {
          this.handleAuthError(res);
          this.dailySummary = null;
          this.dailyMetricRows = [];
        }
      },
      () => {
        this.dailySummary = null;
        this.dailyMetricRows = [];
      }
    );
  }

  getTopPlayersForDay(dateStr: string) {
    const req = this.withOperatorFilter(
      {
        ...this.dayBounds(dateStr),
        limit: 10,
      },
      this.playerOperator
    );
    this.appService.getOpTopPlayers(req).subscribe(
      (res) => {
        if (res.status === "RS_OK") {
          this.playerRows = res.rows || [];
        } else {
          this.handleAuthError(res);
          this.playerRows = [];
        }
      },
      () => {
        this.playerRows = [];
      }
    );
  }

  formatNumber(value: number) {
    if (value === null || value === undefined) {
      return "0";
    }
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}
